import { useCallback, useEffect, useState } from 'react';
import { GroupStatsProps } from '../GroupTabs.types';
import moment from 'moment';
import useBaseRequest from '../../../../api/BaseRequest';
import { IGroupMemberStats, IGroupSeason } from '@grassrootly/api';
import { GetGroupMembersByDate, GetGroupMembersByDateParams, GetGroupSeasons } from '@grassrootly/api/providers/supabase-provider/actions';
import { GetGroupSeasonsPayload } from '@grassrootly/api';
import { useFocusEffect } from '@react-navigation/native';

export interface IGroupStatsHook {
  topTeam: IGroupMemberStats[];
  monthlyMembers: any[];
  month: string;
  navigateMonth: (increment: number) => void;
  loadingInProgress: boolean;
  mode: "monthly" | "yearly" | "seasonal" | "alltime";
  setMode: (m: "monthly" | "yearly" | "seasonal" | "alltime") => void;
  selectedSeason: string;
  setSelectedSeason: (v: string) => void;
  seasonOptions: { value: string; label: string }[];
  selectedSeasonIsActive: boolean;
}

const TOP_PLAYER_STATS_COUNT = 6;

export function useGroupStats({ group }: GroupStatsProps): IGroupStatsHook {
  const [fromDate, setFromDate] = useState<string>(moment().startOf("month").format("YYYY-MM-DDTHH:mm:ss"));
  const [toDate, setToDate] = useState<string>(moment().endOf("month").format("YYYY-MM-DDTHH:mm:ss"));
  const [month, setMonth] = useState<string>(moment().format("MMMM YYYY"));
  const [externalMonthNavigation, setExternalMonthNavigation] = useState<boolean>(false);
  const [mode, setMode] = useState<"monthly" | "yearly" | "seasonal" | "alltime">("monthly");
  const [seasonOptions, setSeasonOptions] = useState<{ value: string; label: string; seasonId: number; isActive: boolean; icon?: string }[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<string>("");
  const [selectedSeasonId, setSelectedSeasonId] = useState<number | undefined>(undefined);
  const [selectedSeasonIsActive, setSelectedSeasonIsActive] = useState<boolean>(true);

  const [topTeam, setTopTeam] = useState<any>([]);
  const [monthlyMembers, setMonthlyMembers] = useState<any[]>([]);
  const [loadingInProgress, setLoadingInProgress] = useState<boolean>(false);

  // Fetch group seasons
  const { loading: loadingSeasons, execute: getGroupSeasons } = useBaseRequest(GetGroupSeasons, {
    onCompleted: async (data: IGroupSeason[]) => {
      const options = data
        .map(season => ({
          value: season.season_summary,
          label: season.season_summary,
          seasonId: season.season_id,
          isActive: season.season_is_active,
          icon: season.organization_logo || undefined
        }))
        .sort((a, b) => {
          // Active seasons first, then alphabetically by summary
          if (a.isActive && !b.isActive) return -1;
          if (!a.isActive && b.isActive) return 1;
          return a.label.localeCompare(b.label);
        });
      setSeasonOptions(options);
      
      // Set first season as default if no season is selected
      if (!selectedSeason && options.length > 0) {
        setSelectedSeason(options[0].value);
        setSelectedSeasonId(options[0].seasonId);
        setSelectedSeasonIsActive(options[0].isActive);
      }
      
      // If seasonal mode is selected but no seasons available, switch to monthly
      if (mode === "seasonal" && options.length === 0) {
        setMode("monthly");
      }
    },
  });

  // Fetch seasons when group changes
  useEffect(() => {
    if (group?.id) {
      const params: GetGroupSeasonsPayload = {
        groupId: group.id,
      };
      getGroupSeasons(params);
    }
  }, [group?.id]);

  useFocusEffect(useCallback(() => {
    if (group) {
      if (mode === "monthly") {
        setFromDate(moment().startOf("month").format("YYYY-MM-DDTHH:mm:ss"));
        setToDate(moment().endOf("month").format("YYYY-MM-DDTHH:mm:ss"));
        setMonth(moment().format("MMMM YYYY"));
      } else if (mode === "yearly") {
        setFromDate(moment().startOf("year").format("YYYY-MM-DDTHH:mm:ss"));
        setToDate(moment().endOf("year").format("YYYY-MM-DDTHH:mm:ss"));
        setMonth(moment().format("YYYY"));
      } else if (mode === "seasonal") {
        // seasonal: default to current year range until real season ranges are wired
        setFromDate(moment().startOf("year").format("YYYY-MM-DDTHH:mm:ss"));
        setToDate(moment().endOf("year").format("YYYY-MM-DDTHH:mm:ss"));
        setMonth(selectedSeason);
      } else if (mode === "alltime") {
        // alltime: no date filters, show all data
        setFromDate("");
        setToDate("");
        setMonth("All Time");
      }
      setExternalMonthNavigation(false);
    }
  }, [group, mode, selectedSeason]));

  // Also react immediately when mode changes while screen is focused
  useEffect(() => {
    if (!group) return;
    
    // Set loading state immediately when mode changes
    setLoadingInProgress(true);
    
    if (mode === "monthly") {
      setFromDate(moment().startOf("month").format("YYYY-MM-DDTHH:mm:ss"));
      setToDate(moment().endOf("month").format("YYYY-MM-DDTHH:mm:ss"));
      setMonth(moment().format("MMMM YYYY"));
    } else if (mode === "yearly") {
      setFromDate(moment().startOf("year").format("YYYY-MM-DDTHH:mm:ss"));
      setToDate(moment().endOf("year").format("YYYY-MM-DDTHH:mm:ss"));
      setMonth(moment().format("YYYY"));
    } else if (mode === "seasonal") {
      // seasonal: default to current year range until real season ranges are wired
      setFromDate(moment().startOf("year").format("YYYY-MM-DDTHH:mm:ss"));
      setToDate(moment().endOf("year").format("YYYY-MM-DDTHH:mm:ss"));
      setMonth(selectedSeason);
    } else if (mode === "alltime") {
      // alltime: no date filters, show all data
      setFromDate("");
      setToDate("");
      setMonth("All Time");
    }
    setExternalMonthNavigation(false);
  }, [mode, selectedSeason]);

  useEffect(() => {
    if (group) {
      // Add a small delay to ensure state updates are complete before API call
      const timeoutId = setTimeout(() => {
        const params: GetGroupMembersByDateParams = mode === "seasonal" && selectedSeasonId 
          ? {
              gid: group.id,
              p_season_id: selectedSeasonId
            }
          : mode === "alltime"
          ? {
              gid: group.id
              // No date filters for all time
            }
          : {
              gid: group.id,
              p_date_from: fromDate,
              p_date_to: toDate
            };
        
        // Only make API call if we have valid dates for non-alltime modes
        const shouldMakeCall = mode === "alltime" || 
                              (mode === "seasonal" && selectedSeasonId) || 
                              ((mode === "monthly" || mode === "yearly") && fromDate && toDate);
        
        if (shouldMakeCall) {
          getGroupMembersByDate(params);
        }
      }, 50); // Small delay to ensure state updates are complete
      
      return () => clearTimeout(timeoutId);
    }
  }, [fromDate, toDate, group, selectedSeasonId, mode]);

  const { loading: loadingByDate, execute: getGroupMembersByDate } = useBaseRequest(GetGroupMembersByDate, {
    onCompleted: async (data: any[]) => {
      const list = Array.isArray(data) ? data : [];
      // Filter out players with 0 games first
      const playersWithGames = list.filter((it: any) => Number(it?.games_played ?? 0) > 0);
      
      // Auto-fallback to previous month if current month has no players with games
      const selectedUnitValue = mode === "monthly" ? moment(fromDate).month() : moment(fromDate).year();
      const currentUnitValue = mode === "monthly" ? moment().month() : moment().year();
      if (playersWithGames.length === 0 && !externalMonthNavigation && selectedUnitValue === currentUnitValue) {
        setExternalMonthNavigation(true); // Prevent infinite loop
        navigateMonth(-1);
        return; // Exit early to avoid processing empty data
      }
      
      setMonthlyMembers(list);
      // Apply dynamic percentage of MAX_GAMES rule: start at 50% of the most active
      // player's games, then relax to 40%, 30%, and finally 20% until we have at
      // least TOP_PLAYER_STATS_COUNT eligible players. If there are still fewer
      // than TOP_PLAYER_STATS_COUNT players at 20%, return as many as qualify.
      const maxGames = playersWithGames.reduce(
        (m: number, it: any) => Math.max(m, Number(it?.games_played ?? 0)),
        0
      );

      let eligibleForTeam = playersWithGames;
      if (maxGames > 0) {
        const thresholds = [0.5, 0.4, 0.3, 0.2];

        for (let i = 0; i < thresholds.length; i++) {
          const threshold = thresholds[i];
          const minRequiredGames = Math.ceil(threshold * maxGames);
          const candidates = playersWithGames.filter(
            (it: any) => Number(it?.games_played ?? 0) >= minRequiredGames
          );

          const isLastThreshold = i === thresholds.length - 1;
          if (candidates.length >= TOP_PLAYER_STATS_COUNT || isLastThreshold) {
            eligibleForTeam = candidates;
            break;
          }
        }
      }

      const sorted = eligibleForTeam.slice().sort((x: any, y: any) => {
        if (!x.avg_rating || !y.avg_rating) return 0;
        if (Number(x.avg_rating) > Number(y.avg_rating)) return -1;
        if (Number(x.avg_rating) < Number(y.avg_rating)) return 1;
        return 0;
      });
      // Map API fields to what GroupTopTeam expects
      const mappedTopTeam = sorted
        .slice(0, TOP_PLAYER_STATS_COUNT)
        .map((item: any) => ({
          ...item,
          name: item.name ?? item.player_name ?? "",
          profile_picture: item.profile_picture ?? item.player_profile_picture ?? undefined,
        }));
      setTopTeam(mappedTopTeam);
      setTimeout(() => setLoadingInProgress(false), 300);
    },
  });

  const navigateMonth = (increment: number) => {
    if (mode === "monthly") {
      const newFromDate = moment(fromDate).add(increment, "month").startOf("month").format("YYYY-MM-DDTHH:mm:ss");
      const newToDate = moment(fromDate).add(increment, "month").endOf("month").format("YYYY-MM-DDTHH:mm:ss");
      const newLabel = moment(fromDate).add(increment, "month").format("MMMM YYYY");
      setFromDate(newFromDate);
      setToDate(newToDate);
      setMonth(newLabel);
    } else {
      const newFromDate = moment(fromDate).add(increment, "year").startOf("year").format("YYYY-MM-DDTHH:mm:ss");
      const newToDate = moment(fromDate).add(increment, "year").endOf("year").format("YYYY-MM-DDTHH:mm:ss");
      const newLabel = moment(fromDate).add(increment, "year").format("YYYY");
      setFromDate(newFromDate);
      setToDate(newToDate);
      setMonth(newLabel);
    }
  };

  const navigateMonthExternal = (increment: number) => {
    setExternalMonthNavigation(true);
    navigateMonth(increment);
  };

  const handleSeasonChange = (seasonValue: string) => {
    setSelectedSeason(seasonValue);
    const selectedOption = seasonOptions.find(option => option.value === seasonValue);
    if (selectedOption) {
      setSelectedSeasonId(selectedOption.seasonId);
      setSelectedSeasonIsActive(selectedOption.isActive);
    }
  };

  return {
    topTeam,
    monthlyMembers,
    month,
    navigateMonth: navigateMonthExternal,
    loadingInProgress,
    mode,
    setMode,
    selectedSeason,
    setSelectedSeason: handleSeasonChange,
    seasonOptions,
    selectedSeasonIsActive,
  };
}